import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
} from '@frontend/shadcn/components/ui/sheet';
import { Button } from '@frontend/shadcn/components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@frontend/shadcn/components/ui/tabs';
import { Alert, AlertDescription } from '@frontend/shadcn/components/ui/alert';
import {
  XmarkIcon,
  ArrowLeftIcon,
  EyeIcon,
  CircleInfoIcon,
} from '@revfluence/fresh-icons/solid/esm';
import { InstagramCreator } from '@frontend/gql/social/graphql';
import { CreatorDiscoveryEmailResource } from '@frontend/app/hooks';
import { QueryVariables } from '../types';
import { CreatorProfileCard } from './components/creator-profile-card';
import { useCreatorProfileCardProps } from './components/creator-profile-card/hooks';
import { useRecentPostsProps } from './components/recent-posts-section/hooks';
import { RecentPostsSection } from './components/recent-posts-section';
import { useMajorAudienceDemographicsProps } from './components/major-audience-demographics-section/hooks';
import { MajorAudienceDemographicsSection } from './components/major-audience-demographics-section';
import { useSocialStatsProps } from './components/social-stats-section/hooks';
import { SocialStatsSection } from './components/social-stats-section';
import { CreatorAvatarHeader } from './components/creator-avatar-header/creator-avatar-header';
import { useCreatorAvatarHeaderProps } from './components/creator-avatar-header/hooks';
import { SimilarCreatorsSection } from './components/similar-creators-section';
import { CreatorOverviewActionMenu } from './components/creator-overview-action-menu';
import { useCreatorOverviewActions } from './hooks/use-creator-overview-actions';

interface InstagramCreatorOverviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creator: InstagramCreator | null;
  isLoading: boolean;
  isLoadingDetailedData?: boolean;
  isFetchingSimilarCreators: boolean;
  similarCreators: InstagramCreator[] | null;
  onCreatorChange?: (creator: Pick<InstagramCreator, 'username'>) => void;
  clickedCreators: InstagramCreator[];
  originalCreator: Pick<InstagramCreator, 'username'> | null;
  onReturnToOriginalCreator: () => void;
  showReturnButton: boolean;
  updateUrlParams?: (params: Partial<QueryVariables>) => void;
  handleSearch?: () => void;
  queryVariables?: QueryVariables;
  onInviteCreator?: () => void | Promise<void>;
  isInviting?: boolean;
  isCreatorInvited?: boolean;
  emailResource?: CreatorDiscoveryEmailResource;
  igdmActive?: boolean;
}

export const InstagramCreatorOverview: React.FC<InstagramCreatorOverviewProps> = ({
  open,
  onOpenChange,
  creator,
  isLoading,
  isLoadingDetailedData,
  isFetchingSimilarCreators,
  similarCreators,
  onCreatorChange,
  clickedCreators,
  originalCreator,
  onReturnToOriginalCreator,
  showReturnButton,
  updateUrlParams,
  handleSearch,
  onInviteCreator,
  isInviting = false,
  isCreatorInvited = false,
  emailResource,
  igdmActive = false,
}) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [showVerificationAlert, setShowVerificationAlert] = useState(true);

  // Initialize action menu hook
  const actionMenuProps = useCreatorOverviewActions({
    creator,
    emailResource,
    igdmActive,
  });

  useEffect(() => {
    if (!open) {
      setActiveTab('summary');
      setShowVerificationAlert(true);
    }
  }, [open]);

  const creatorAvatarHeaderProps = useCreatorAvatarHeaderProps(
    creator,
    isLoading,
    clickedCreators,
    originalCreator,
    onReturnToOriginalCreator
  );
  const profileCardProps = useCreatorProfileCardProps(creator, isLoading);
  const recentPostsProps = useRecentPostsProps(creator, isLoadingDetailedData ?? isLoading);
  const majorAudienceDemographicsProps = useMajorAudienceDemographicsProps(creator, isLoadingDetailedData ?? isLoading);
  const socialStatsProps = useSocialStatsProps(isLoadingDetailedData ?? isLoading, creator);

  const handleCreatorClick = (clickedCreator: InstagramCreator) => {
    setActiveTab('summary');
    if (onCreatorChange) {
      onCreatorChange({ username: clickedCreator.username });
    }
  };

  const handleViewMore = () => {
    if (creator && updateUrlParams && handleSearch) {
      // Add current creator's username to similarToCreators and update search
      updateUrlParams({
        similarToCreators: [creator.username],
        creatorUsername: '', // Clear to close the overview
        // Reset pagination
        before: null,
        after: null,
      });
      handleSearch();
    }
  };

  const handleDismissVerificationAlert = () => {
    setShowVerificationAlert(false);
  };

  const sheetTitleText = 'Creator Overview';
  const sheetDescriptionText = creator ? `Detailed overview for Instagram creator @${creator.username}.` : 'Detailed overview of an Instagram creator.';
  const sheetDescriptionLoadingText = 'Loading creator overview details.';

  const sheetContent = (() => {
    if (isLoading) {
      return (
        <Sheet open={open} onOpenChange={onOpenChange}>
          <SheetContent className="w-full overflow-y-auto p-0 sm:max-w-lg md:max-w-xl lg:max-w-2xl z-[200]" side="right">
            <SheetHeader className="px-4 py-3 border-b sticky top-0 bg-background z-10">
              <div className="flex justify-between items-center">
                <div>
                  <SheetTitle className="text-lg font-semibold flex items-center gap-3">
                    {showReturnButton && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={onReturnToOriginalCreator}
                        className="p-2 border border-[#16282D] rounded-lg"
                        style={{ borderRadius: '8px', border: '1px solid #16282D', padding: '8px' }}
                        aria-label={`Return to @${originalCreator.username}'s profile`}
                        data-dd-action-name="instagram-creator-overview-return-to-original"
                      >
                        <ArrowLeftIcon className="h-4 w-4" />
                      </Button>
                    )}
                    {sheetTitleText}
                  </SheetTitle>
                  <SheetDescription className="sr-only">{sheetDescriptionLoadingText}</SheetDescription>
                </div>
                <SheetClose asChild>
                  <Button variant="ghost" size="icon" aria-label="Close overview" disabled data-dd-action-name="instagram-creator-overview-close">
                    <XmarkIcon className="h-5 w-5" />
                  </Button>
                </SheetClose>
              </div>
            </SheetHeader>
            <div className="space-y-6 flex-grow bg-gray-100">
              <CreatorAvatarHeader {...creatorAvatarHeaderProps} />

              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <div className="flex justify-between items-center px-4">
                  <TabsList className="h-12 bg-transparent shadow-none rounded-none">
                    <TabsTrigger
                      value="summary"
                      className="data-[state=active]:border-b-2 data-[state=active]:border-[#16282D] data-[state=active]:bg-transparent rounded-none shadow-none data-[state=active]:border-t-0 data-[state=active]:border-l-0 data-[state=active]:border-r-0 data-[state=active]:shadow-none"
                      data-dd-action-name="instagram-creator-overview-summary-tab"
                    >
                      Summary
                    </TabsTrigger>
                    <TabsTrigger
                      value="similar"
                      className="data-[state=active]:border-b-2 data-[state=active]:border-[#16282D] data-[state=active]:bg-transparent rounded-none shadow-none data-[state=active]:border-t-0 data-[state=active]:border-l-0 data-[state=active]:border-r-0 data-[state=active]:shadow-none"
                      data-dd-action-name="instagram-creator-overview-similar-creators-tab"
                    >
                      Similar Creators
                    </TabsTrigger>
                  </TabsList>
                  <div className="flex items-center gap-2">
                    {creator && (
                      <CreatorOverviewActionMenu
                        creator={creator}
                        igdmActive={igdmActive}
                        disabled={isLoading}
                        {...actionMenuProps}
                      />
                    )}
                    {activeTab === 'similar' && (
                      <Button
                        variant="outline"
                        className="inline-flex items-center gap-2"
                        style={{
                          borderRadius: '8px',
                          border: '1px solid #E5E8EB',
                          background: '#FFF',
                          padding: '4px 12px'
                        }}
                        onClick={handleViewMore}
                        disabled={isLoading || !creator || !updateUrlParams || !handleSearch}
                        data-dd-action-name="instagram-creator-overview-view-more-similar-creators"
                      >
                        View More
                        <EyeIcon className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                <TabsContent value="summary">
                  <CreatorProfileCard {...profileCardProps} />
                  <RecentPostsSection {...recentPostsProps} />
                  <MajorAudienceDemographicsSection {...majorAudienceDemographicsProps} />
                  <SocialStatsSection {...socialStatsProps} />
                </TabsContent>
                <TabsContent value="similar">
                  <SimilarCreatorsSection
                    similarCreators={similarCreators}
                    isFetching={isFetchingSimilarCreators}
                    onCreatorClick={handleCreatorClick}
                  />
                </TabsContent>
              </Tabs>

              <div className="h-10" />
            </div>
          </SheetContent>
        </Sheet>
      );
    }

    if (!creator) {
      return null;
    }

    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full overflow-y-auto p-0 sm:max-w-lg md:max-w-xl lg:max-w-2xl z-[200]" side="right">
          <div className="flex flex-col h-full">
            <SheetHeader className="px-4 py-3 border-b sticky top-0 bg-background z-10">
              <div className="flex justify-between items-center">
                <div>
                  <SheetTitle className="text-lg font-semibold flex items-center gap-3 mb-0">
                    {showReturnButton && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={onReturnToOriginalCreator}
                        className="p-2 border border-[#16282D] rounded-lg"
                        style={{ borderRadius: '8px', border: '1px solid #16282D', padding: '8px' }}
                        aria-label={`Return to @${originalCreator.username}'s profile`}
                        data-dd-action-name="instagram-creator-overview-return-to-original"
                      >
                        <ArrowLeftIcon className="h-4 w-4" />
                      </Button>
                    )}
                    {sheetTitleText}
                  </SheetTitle>
                  <SheetDescription className="sr-only">{sheetDescriptionText}</SheetDescription>
                </div>
                <SheetClose asChild>
                  <Button variant="ghost" size="icon" aria-label="Close overview" data-dd-action-name="instagram-creator-overview-close">
                    <XmarkIcon className="h-5 w-5" />
                  </Button>
                </SheetClose>
              </div>
            </SheetHeader>

            <div className="space-y-2 flex-grow pb-5 bg-white">
              <CreatorAvatarHeader {...creatorAvatarHeaderProps} />

              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <div className="flex justify-between items-center px-4">
                  <TabsList className="h-12 bg-transparent shadow-none rounded-none">
                    <TabsTrigger
                      value="summary"
                      className="data-[state=active]:border-b-2 data-[state=active]:border-[#16282D] data-[state=active]:bg-transparent rounded-none shadow-none data-[state=active]:border-t-0 data-[state=active]:border-l-0 data-[state=active]:border-r-0 data-[state=active]:shadow-none"
                      data-dd-action-name="instagram-creator-overview-summary-tab"
                    >
                      Summary
                    </TabsTrigger>
                    <TabsTrigger
                      value="similar"
                      className="data-[state=active]:border-b-2 data-[state=active]:border-[#16282D] data-[state=active]:bg-transparent rounded-none shadow-none data-[state=active]:border-t-0 data-[state=active]:border-l-0 data-[state=active]:border-r-0 data-[state=active]:shadow-none"
                      data-dd-action-name="instagram-creator-overview-similar-creators-tab"
                    >
                      Similar Creators
                    </TabsTrigger>
                  </TabsList>
                  <div className="flex items-center gap-2">
                    {creator && (
                      <CreatorOverviewActionMenu
                        creator={creator}
                        igdmActive={igdmActive}
                        disabled={isLoading}
                        {...actionMenuProps}
                      />
                    )}
                    {activeTab === 'similar' && (
                      <Button
                        variant="outline"
                        className="inline-flex items-center gap-2"
                        style={{
                          borderRadius: '8px',
                          border: '1px solid #E5E8EB',
                          background: '#FFF',
                          padding: '4px 12px'
                        }}
                        onClick={handleViewMore}
                        disabled={isLoading || !creator || !updateUrlParams || !handleSearch}
                        data-dd-action-name="instagram-creator-overview-view-more-similar-creators"
                      >
                        View More
                        <EyeIcon className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {creator && !creator.inCreatorMarketplace && showVerificationAlert && !isLoading && (
                  <div className="px-4 py-2">
                    <Alert className="flex items-center justify-center gap-2 h-[58px] w-full px-4 py-2 rounded-md border border-amber-300 bg-amber-50">
                      <AlertDescription className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                          <CircleInfoIcon className="w-4 h-4 flex-shrink-0 text-amber-500" />
                          <span className="text-sm text-gray-900">
                            This creator isn't onboarded to the Instagram Creator Marketplace.
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {isCreatorInvited ? (
                            <span className="text-sm text-green-600 font-medium">
                              Invited
                            </span>
                          ) : (
                            <Button
                              variant="link"
                              size="sm"
                              className="text-blue-600 p-0 h-auto font-medium"
                              onClick={onInviteCreator}
                              disabled={isInviting}
                              data-dd-action-name="instagram-creator-overview-invite-creator"
                            >
                              {isInviting ? 'Inviting...' : 'Invite'}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleDismissVerificationAlert}
                            className="p-1 h-auto text-gray-400 hover:text-gray-600"
                            aria-label="Dismiss alert"
                            data-dd-action-name="instagram-creator-overview-dismiss-verification-alert"
                          >
                            <XmarkIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </AlertDescription>
                    </Alert>
                  </div>
                )}

                <TabsContent value="summary">
                  <CreatorProfileCard {...profileCardProps} />
                  <RecentPostsSection {...recentPostsProps} />
                  <MajorAudienceDemographicsSection {...majorAudienceDemographicsProps} />
                  <SocialStatsSection {...socialStatsProps} />
                </TabsContent>
                <TabsContent value="similar">
                  <SimilarCreatorsSection
                    similarCreators={similarCreators}
                    isFetching={isFetchingSimilarCreators}
                    onCreatorClick={handleCreatorClick}
                  />
                </TabsContent>
              </Tabs>

              <div className="h-10" />
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  })();

  return sheetContent;
};

export default InstagramCreatorOverview;
